import { createContext, useContext, useCallback } from "react";
import { useAuthProvider } from "./AuthProvider";
import { useWorkspacesProvider } from "./WorkspacesProvider";

const EndpointContext = createContext()

// Allow a post request to be made from above the EndpointContext.
// Because EndpointProvider cannot be used, this method cannot be a callback.
// DO NOT USE THIS FUNCTION if the React component already has access to EndpointProvider.
// This is a special exception, intended for use by AuthProvider.
export const postWithDependencyInjection = async (authToken, workspaceName, ENDPOINT, body=undefined, errorCodeHandlerMap=new Map(), signal=undefined, method="POST") => {
    if (ENDPOINT.REQUIRES_TOKEN && !authToken) {
        return new Promise((_, reject) => reject('This endpoint requires a token and there is not one present or the user is not logged in.'))
    }    
    
    if (!body) {
        body = {}
    }

    if (!Object.keys(body).includes("workspace_name")) {
        body["workspace_name"] = workspaceName
    }

    const fullFetch = `${ENDPOINT.LINK}`

    return fetch(fullFetch, {
        method,
        cache: "no-cache",
        credentials: "same-origin", // TODO: Remove?
        headers: {
            "content-type": "application/json",
            Authorization: authToken,
        },
        body: JSON.stringify(body),
        signal: signal  // Pass AbortSignal for request cancellation
    }).then(async response => {
        if (!response.ok) {
            if (errorCodeHandlerMap.has(response.status)) {
                const listener = errorCodeHandlerMap.get(response.status)

                let payload, errorMessage

                switch(ENDPOINT.ERROR_RESPONSE_TYPE) {
                    case "json":
                        payload = JSON.parse(await response.text())
                        errorMessage = payload.errorMessage
                        break
                    case "text":
                    default:
                        payload = await response.text()
                        errorMessage = payload
                }

                listener(payload)
                
                return new Promise((_, reject) => reject(errorMessage))
            }

            return new Promise(async (_, reject) => reject(await response.text()))
        }
        
        if (ENDPOINT.OUTPUT_TYPE === 'json') {
            return await response.json()
        } else if (ENDPOINT.OUTPUT_TYPE === 'text') {
            return await response.text()
        } else if (ENDPOINT.OUTPUT_TYPE === 'file') {
            return await response.blob();
        }

        return response
    }).catch(err => {
        // Don't log abort errors as they are expected when requests are cancelled
        if (err.name === 'AbortError') {
            console.log('Request was cancelled');
        }
        return new Promise((_, reject) => reject(err))
    })
}

export const getWithDependencyInjection = async (authToken, workspaceName, ENDPOINT, params = {}, signal=undefined) => {
    if (!Object.keys(params).includes("workspace_name")) {
        params["workspace_name"] = workspaceName
    }

    const paramString = `?${Object.entries(params).map(([param, value]) => `${param}=${value}`).join("&")}`
    const fullFetch = `${ENDPOINT.LINK}${paramString}`

    const buildRequest = () => fetch(fullFetch, {
        method: "GET",
        headers: {
            Authorization: authToken
        },
        signal: signal  // Pass AbortSignal for request cancellation
    })

    const handleSuccess = async response => {
        if (!response.ok) {
            return new Promise(async (_, reject) => reject(await response.text()))
        }

        if (ENDPOINT.OUTPUT_TYPE === 'json') {
            return await response.json()
        } else if (ENDPOINT.OUTPUT_TYPE === 'text') {
            return await response.text()
        }

        return response
    }

    return buildRequest().then(handleSuccess).catch(err => {
        // Don't log abort errors as they are expected when requests are cancelled
        if (err.name === 'AbortError') {
            console.log('Request was cancelled');
        }
        throw err;
    })
}

export const EndpointProvider = ({ children }) => {
    console.log("PROVIDER RENDER: ENDPOINT")

    const authProvider = useAuthProvider()
    const workspaceProvider = useWorkspacesProvider()

    const post = useCallback(async (ENDPOINT, body = {}, errorHandlerMap, signal) => {
        if (ENDPOINT == null) {
            // Defensive: callers should never pass null. Reject cleanly instead
            // of crashing on the dereference below so React Query consumers see
            // a normal rejection rather than an unhandled TypeError.
            return Promise.reject(new Error("endpointProvider.post called with no ENDPOINT"))
        }
        if (ENDPOINT.REQUIRES_TOKEN && (!authProvider.getToken() || !authProvider.userIsLoggedIn)) {
            return new Promise((_, reject) => reject('This endpoint requires a token and there is not one present or the user is not logged in.'))
        }
        return postWithDependencyInjection(authProvider.getToken(), workspaceProvider.selectedWorkspace, ENDPOINT, body, errorHandlerMap, signal, "POST")
    }, [authProvider, workspaceProvider.selectedWorkspace])

    const patch = useCallback(async (ENDPOINT, body = {}, errorHandlerMap, signal) => {
        if (ENDPOINT == null) {
            return Promise.reject(new Error("endpointProvider.patch called with no ENDPOINT"))
        }
        if (ENDPOINT.REQUIRES_TOKEN && (!authProvider.getToken() || !authProvider.userIsLoggedIn)) {
            return new Promise((_, reject) => reject('This endpoint requires a token and there is not one present or the user is not logged in.'))
        }
        return postWithDependencyInjection(authProvider.getToken(), workspaceProvider.selectedWorkspace, ENDPOINT, body, errorHandlerMap, signal, "PATCH")
    }, [authProvider, workspaceProvider.selectedWorkspace])

    const del = useCallback(async (ENDPOINT, body = {}, errorHandlerMap, signal) => {
        if (ENDPOINT == null) {
            return Promise.reject(new Error("endpointProvider.del called with no ENDPOINT"))
        }
        if (ENDPOINT.REQUIRES_TOKEN && (!authProvider.getToken() || !authProvider.userIsLoggedIn)) {
            return new Promise((_, reject) => reject('This endpoint requires a token and there is not one present or the user is not logged in.'))
        }
        return postWithDependencyInjection(authProvider.getToken(), workspaceProvider.selectedWorkspace, ENDPOINT, body, errorHandlerMap, signal, "DELETE")
    }, [authProvider, workspaceProvider.selectedWorkspace])

    const get = useCallback(async (ENDPOINT, params = {}, signal) => {
        if (ENDPOINT == null) {
            return Promise.reject(new Error("endpointProvider.get called with no ENDPOINT"))
        }
        if (ENDPOINT.REQUIRES_TOKEN && (!authProvider.getToken() || !authProvider.userIsLoggedIn)) {
            return new Promise((_, reject) => reject('This endpoint requires a token and there is not one present or the user is not logged in.'))
        }
        return getWithDependencyInjection(authProvider.getToken(), workspaceProvider.selectedWorkspace, ENDPOINT, params, signal)
    }, [authProvider, workspaceProvider.selectedWorkspace])

    return (
        <EndpointContext.Provider value={{ post, get, patch, del }}>
            {children}
        </EndpointContext.Provider>
    )
}

export const useEndpointProvider = () => {
    return useContext(EndpointContext)
}